import { useState, useEffect, useMemo, useRef } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Loader2, Home, UserPlus, Users, Search, UserCheck, History, X, CheckCircle, Edit, Trash2 } from "lucide-react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, getDay, startOfWeek, endOfWeek, startOfDay, endOfDay, isWithinInterval, addWeeks, subWeeks, addDays, subDays, formatDistanceToNow } from "date-fns";
import { CalendarSidebar, type CalendarItem } from '../components/calendar/CalendarSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import TopNav from '../components/layout/top-nav';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import { useLocation } from "wouter";
import { useEvents } from '../hooks/use-events';
import { useToast } from '../hooks/use-toast';
import EventDialog from '../components/events/event-dialog';
import AgentScheduler from '../components/agents/AgentScheduler';
import ClientForm from '../components/clients/client-form';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '../lib/queryClient';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatPhone } from '../lib/validation';
import { useAuth } from '../hooks/use-auth';

// Event interface
interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  time: string;
  timeColor: string;
  location: string;
  type: "meeting" | "site-visit" | "delivery" | "estimate" | "invoice" | "other";
  status: "confirmed" | "pending" | "completed" | "cancelled";
  contact?: {
    name: string;
    avatar?: string;
    initials?: string;
    id?: number;
  };
  orderNumber?: string;
  clientId?: number;
  projectId?: number;
  description?: string;
  agentId?: number;
  agentName?: string | null;
  estimateId?: number;
  isProject?: boolean;
}

const EVENT_TYPE_CALENDARS: { id: string; type: CalendarEvent["type"]; label: string; color: string }[] = [
  { id: "type-estimate", type: "estimate", label: "Estimate", color: "#3b82f6" },
  { id: "type-site-visit", type: "site-visit", label: "Site Visit", color: "#f59e0b" },
  { id: "type-meeting", type: "meeting", label: "Meeting", color: "#8b5cf6" },
  { id: "type-delivery", type: "delivery", label: "Delivery", color: "#10b981" },
  { id: "type-invoice", type: "invoice", label: "Invoice", color: "#ef4444" },
  { id: "type-other", type: "other", label: "Other", color: "#64748b" },
];
const CATEGORIES = [
  { id: "calendar", label: "Calendar", color: "#3b82f6" },
  { id: "projects", label: "Calendar Projects", color: "#10b981" },
];

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"month" | "day" | "week">("month");
  const [search, setSearch] = useState("");
  const [calendarSearch, setCalendarSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<Set<string>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const [isNewEventDialogOpen, setIsNewEventDialogOpen] = useState(false);
  const [isEditEventDialogOpen, setIsEditEventDialogOpen] = useState(false);
  const [isClientFormOpen, setIsClientFormOpen] = useState(false);
  const [currentEventId, setCurrentEventId] = useState<number | undefined>(undefined);
  const [defaultClientId, setDefaultClientId] = useState<string | undefined>(undefined);
  const [defaultEventType, setDefaultEventType] = useState<string | undefined>("estimate");
  const [defaultEventDate, setDefaultEventDate] = useState<Date | null>(null);

  const { user } = useAuth();
  const prefsLoadedRef = useRef(false);

  const prefsCookieKey = user?.id ? `remodra_calendar_prefs_v1_${user.id}` : null;

  const toISODateOnly = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : null);
  const parseISODateOnly = (raw: unknown): Date | null => {
    if (typeof raw !== "string") return null;
    const str = raw.trim();
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const [, y, mo, da] = m;
    const yy = Number(y);
    const mm = Number(mo);
    const dd = Number(da);
    if (!yy || !mm || !dd) return null;
    // Use noon to avoid DST issues when formatting/reading.
    return new Date(yy, mm - 1, dd, 12, 0, 0, 0);
  };

  // Load persisted calendar selection (sidebar checkboxes, category chip, view, and selected date)
  useEffect(() => {
    if (!prefsCookieKey) return;
    if (typeof window === "undefined") return;

    try {
      const getCookie = (name: string): string | null => {
        const cookies = typeof document !== "undefined" ? document.cookie.split(";").map((c) => c.trim()) : [];
        const prefix = `${name}=`;
        for (const c of cookies) {
          if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
        }
        return null;
      };

      const raw = getCookie(prefsCookieKey);
      if (!raw) {
        prefsLoadedRef.current = true;
        return;
      }

      const parsed = JSON.parse(raw) as {
        selectedCalendarIds?: string[];
        selectedCategoryId?: string | null;
        view?: "month" | "day" | "week";
        currentDate?: string | null;
        selectedDate?: string | null;
      };

      if (parsed.view === "month" || parsed.view === "day" || parsed.view === "week") setView(parsed.view);
      if (typeof parsed.selectedCategoryId === "string" || parsed.selectedCategoryId === null) {
        setSelectedCategoryId(parsed.selectedCategoryId ?? null);
      }
      // Empty array means "all calendars selected" (the UI treats size===0 as all).
      if (Array.isArray(parsed.selectedCalendarIds)) setSelectedCalendarIds(new Set(parsed.selectedCalendarIds));

      const cd = parseISODateOnly(parsed.currentDate);
      if (cd) setCurrentDate(cd);

      const sd = parseISODateOnly(parsed.selectedDate);
      if (sd) setSelectedDate(sd);
      else if (parsed.selectedDate === null) setSelectedDate(null);
    } catch (e) {
      // If localStorage is corrupted, ignore and fall back to defaults.
      console.warn("Failed to load calendar prefs:", e);
    } finally {
      prefsLoadedRef.current = true;
    }
  }, [prefsCookieKey]);

  // Persist selection so it survives logout/login until user changes it.
  useEffect(() => {
    if (!prefsCookieKey) return;
    if (typeof window === "undefined") return;
    if (!prefsLoadedRef.current) return;

    const payload = {
      selectedCalendarIds: Array.from(selectedCalendarIds),
      selectedCategoryId: selectedCategoryId ?? null,
      view,
      currentDate: toISODateOnly(currentDate),
      selectedDate: toISODateOnly(selectedDate),
    };

    try {
      const setCookie = (name: string, value: string, days: number) => {
        const maxAge = Math.floor(days * 24 * 60 * 60);
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
      };

      // 180 days is long enough to cover “last login” and far beyond.
      setCookie(prefsCookieKey, JSON.stringify(payload), 180);
    } catch {
      // Ignore cookie failures (private mode, etc.)
    }
  }, [prefsCookieKey, view, selectedCategoryId, currentDate, selectedDate, selectedCalendarIds]);
  
  // Reassignment functionality
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [assignmentData, setAssignmentData] = useState<any>({
    selectedDate: format(currentDate, 'yyyy-MM-dd'),
    appointmentId: '',
    newAgentId: ''
  });

  // Sync assignment date with current calendar date
  useEffect(() => {
    setAssignmentData(prev => ({
      ...prev,
      selectedDate: format(currentDate, 'yyyy-MM-dd')
    }));
  }, [currentDate]);
  
  // Obtener eventos reales desde la API
  const { getEvents, formatEvent, deleteEvent } = useEvents();
  const { data: apiEvents = [], isLoading } = getEvents();
  
  // Fetch event activities for history
  const { data: activities = [], isLoading: isLoadingActivities, error: activitiesError } = useQuery<any[]>({
    queryKey: ['/api/protected/event-activities'],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/protected/event-activities");
        if (!res.ok) {
          throw new Error('Failed to fetch activities');
        }
        return await res.json();
      } catch (error) {
        console.error('Error fetching activities:', error);
        // Return empty array on error, fallback will be handled by API
        return [];
      }
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchInterval: 30000, // Refetch every 30 seconds (reduced from 5 seconds)
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    retry: 1, // Reduce retries from 2 to 1
  });
  
  // Fetch agents for color coding
  const { data: agents = [] } = useQuery<any[]>({
    queryKey: ['/api/protected/agents'],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/agents");
      return await res.json();
    },
  });

  // Fetch clients for event contact names
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ['/api/protected/clients'],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/clients");
      return await res.json();
    },
  });

  // Fetch projects for "Calendar Projects" view (direct query so data is always available)
  const { data: projectsList = [] } = useQuery<any[]>({
    queryKey: ["/api/protected/projects"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/projects");
      return await res.json();
    },
  });

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/protected/clients", data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      setIsClientFormOpen(false);
      toast({
        title: "Success",
        description: "Client created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create client",
        variant: "destructive",
      });
    },
  });

  // Agent color mapping - using database colors
  const getAgentColor = (agentId?: number) => {
    if (!agentId) return '#64748b'; // slate-500 for unassigned (matches design system)
    const agent = agents.find(agent => agent.id === agentId);
    return agent?.colorCode || '#3b82f6'; // blue-500 default (matches Tailwind design system)
  };

  const getAgentName = (agentId?: number) => {
    if (!agentId) return null;
    const agent = agents.find(a => a.id === agentId);
    return agent ? `${agent.firstName} ${agent.lastName}` : null;
  };
  
  // Convertir los eventos de la API al formato que espera el calendario
  // Force refresh to ensure cancelled events are filtered out
  const events: CalendarEvent[] = isLoading ? [] : (() => apiEvents)()
    .filter(event => event.status !== "cancelled")
    .map(event => {
      const agentId = (event as any).agentId;
      const agentName = agentId ? getAgentName(agentId) : null;
      // Find client for contact name
      let contact = undefined;
      if (event.clientId) {
        const client = clients.find((c: any) => c.id === event.clientId);
        if (client) {
          contact = { name: `${client.firstName} ${client.lastName}` };
        }
      }
      return {
        ...formatEvent(event),
        // Override timeColor with agent color if available
        timeColor: getAgentColor(agentId),
        // Add agent info if available
        agentId: agentId,
        agentName: agentName || undefined,
        contact,
      };
    });

  const safeCurrentDate = useMemo(() => {
    const d = currentDate;
    if (d && d instanceof Date && !isNaN(d.getTime())) return d;
    return new Date();
  }, [currentDate]);

  const dateRange = useMemo(() => {
    switch (view) {
      case "day":
        return { start: startOfDay(safeCurrentDate), end: endOfDay(safeCurrentDate) };
      case "week":
        return { start: startOfWeek(safeCurrentDate), end: endOfWeek(safeCurrentDate) };
      case "month":
      default:
        return { start: startOfMonth(safeCurrentDate), end: endOfMonth(safeCurrentDate) };
    }
  }, [view, safeCurrentDate]);

  const myCalendarsList: CalendarItem[] = useMemo(() => {
    const typeItems: CalendarItem[] = EVENT_TYPE_CALENDARS.map((c) => ({
      id: c.id,
      label: c.label,
      color: c.color,
      checked: selectedCalendarIds.size === 0 || selectedCalendarIds.has(c.id),
    }));
    const agentItems: CalendarItem[] = (agents as any[]).map((a: any) => ({
      id: `agent-${a.id}`,
      label: `${a.firstName || ""} ${a.lastName || ""}`.trim() || "Agent",
      color: a.colorCode || "#3b82f6",
      checked: selectedCalendarIds.size === 0 || selectedCalendarIds.has(`agent-${a.id}`),
    }));
    return [...typeItems, ...agentItems];
  }, [agents, selectedCalendarIds]);

  const allCalendarIds = useMemo(
    () => [...EVENT_TYPE_CALENDARS.map((c) => c.id), ...(agents as any[]).map((a: any) => `agent-${a.id}`)],
    [agents]
  );

  const handleCalendarToggle = (id: string, checked: boolean) => {
    setSelectedCalendarIds((prev) => {
      const next = new Set(prev);
      if (prev.size === 0) {
        allCalendarIds.forEach((cid) => next.add(cid));
        next.delete(id);
        return next;
      }
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const parseProjectDate = (raw: string | Date | null | undefined): Date | null => {
    if (raw == null) return null;
    const str = typeof raw === "string" ? raw.trim().slice(0, 10) : "";
    const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, y, m, d] = dateOnlyMatch;
      return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
    }
    const d = new Date(raw);
    return !isNaN(d.getTime()) ? d : null;
  };

  const projectAsCalendarEvents = useMemo((): CalendarEvent[] => {
    if (!Array.isArray(projectsList) || projectsList.length === 0) return [];
    const out: CalendarEvent[] = [];
    const statusPending = "pending" as const;
    const statusCompleted = "completed" as const;
    for (const p of projectsList) {
      const startRaw = p.startDate ?? p.start_date;
      const endRaw = p.endDate ?? p.end_date;
      const start = parseProjectDate(startRaw);
      const end = parseProjectDate(endRaw);
      const client = p.client;
      const contact = client
        ? { name: [client.firstName ?? client.first_name, client.lastName ?? client.last_name].filter(Boolean).join(" ").trim() || "—" }
        : undefined;
      const status = (p.status || "").toLowerCase() === "completed" ? statusCompleted : statusPending;
      const base = {
        title: p.title || "Untitled Project",
        time: "Project",
        timeColor: "#10b981",
        location: "",
        type: "other" as const,
        status: status as "confirmed" | "pending" | "completed" | "cancelled",
        projectId: p.id,
        isProject: true,
        contact,
      };

      if (start && end && start.getTime() <= end.getTime()) {
        const days = eachDayOfInterval({ start, end });
        days.forEach((date) => {
          out.push({
            ...base,
            id: `project-${p.id}-${format(date, "yyyy-MM-dd")}`,
            date,
          });
        });
      } else if (start) {
        out.push({ ...base, id: `project-${p.id}`, date: start });
      } else if (end) {
        out.push({ ...base, id: `project-${p.id}`, date: end });
      } else {
        const fallback = parseProjectDate(p.createdAt ?? p.created_at) || new Date();
        out.push({ ...base, id: `project-${p.id}`, date: fallback });
      }
    }
    return out;
  }, [projectsList]);

  const filteredEvents = useMemo(() => {
    const inRange = events.filter((event) => isWithinInterval(event.date, dateRange));
    const byCalendar =
      selectedCalendarIds.size === 0
        ? inRange
        : inRange.filter((event) => {
              for (const id of selectedCalendarIds) {
              if (id.startsWith("type-")) {
                const typeId = id.replace("type-", "").replace(/-/g, "");
                const typeMap: Record<string, CalendarEvent["type"] | string[]> = {
                  estimate: ["estimate", "appointment"],
                  sitevisit: ["site-visit"],
                  meeting: ["meeting"],
                  delivery: ["delivery"],
                  invoice: ["invoice"],
                  other: ["other"],
                };
                const match = typeMap[typeId];
                if (Array.isArray(match) ? match.includes(event.type) : event.type === match) return true;
              }
              if (id.startsWith("agent-") && event.agentId === parseInt(id.replace("agent-", ""), 10)) return true;
            }
            return false;
          });
    const byCategory =
      selectedCategoryId === "projects"
        ? (() => {
            const eventsWithProject = byCalendar.filter((event) => event.projectId != null && event.projectId > 0);
            const projectEventsInRange = projectAsCalendarEvents.filter((e) => isWithinInterval(e.date, dateRange));
            return [...eventsWithProject, ...projectEventsInRange];
          })()
        : byCalendar; // "calendar" or null = show all

    const searchLower = calendarSearch.trim().toLowerCase();
    if (!searchLower) return byCategory;
    return byCategory.filter((event) => {
      const title = (event.title || "").toLowerCase();
      const contact = (event.contact?.name || "").toLowerCase();
      const location = (event.location || "").toLowerCase();
      return title.includes(searchLower) || contact.includes(searchLower) || location.includes(searchLower);
    });
  }, [events, dateRange, selectedCalendarIds, selectedCategoryId, calendarSearch, projectAsCalendarEvents]);

  const hasEventsOnDate = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
    const fromEvents = events.some((e) => {
      try {
        const d = e.date;
        return d && d instanceof Date && !isNaN(d.getTime()) && isSameDay(d, date);
      } catch {
        return false;
      }
    });
    if (fromEvents) return true;
    if (selectedCategoryId === "projects" && projectAsCalendarEvents.length) {
      return projectAsCalendarEvents.some((e) => isSameDay(e.date, date));
    }
    return false;
  };

  const monthStart = startOfMonth(safeCurrentDate);
  const monthEnd = endOfMonth(safeCurrentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = getDay(monthStart);
  const calendarDays = useMemo(
    () => Array(startDay).fill(null).concat(daysInMonth),
    [startDay, daysInMonth]
  );

  // Handle navigation based on current view
  const navigateNext = () => {
    switch (view) {
      case "day":
        setCurrentDate(addDays(currentDate, 1));
        break;
      case "week":
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case "month":
      default:
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };
  const navigatePrev = () => {
    switch (view) {
      case "day":
        setCurrentDate(subDays(currentDate, 1));
        break;
      case "week":
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case "month":
      default:
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };  
  // Function to check if a date has events (excluding cancelled events); safe for invalid dates
  const hasEvents = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
    return filteredEvents.some((event) => {
      const d = event.date;
      if (!d || !(d instanceof Date) || isNaN(d.getTime())) return false;
      return isSameDay(d, date);
    });
  };

  const getEventsForDay = (date: Date): CalendarEvent[] => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    return filteredEvents.filter((event) => {
      const d = event.date;
      if (!d || !(d instanceof Date) || isNaN(d.getTime())) return false;
      return isSameDay(d, date);
    });
  };
  
  // Handler to create an estimate from an appointment
  const handleCreateEstimate = (clientId: number) => {
    setLocation(`/vendor-estimate-form-simple?clientId=${clientId}`);
  };

  // Manejadores para crear nuevos eventos
  const handleOpenNewEventDialog = (date?: Date) => {
    setCurrentEventId(undefined);
    if (date) {
      setDefaultEventDate(date);
    }
    setIsNewEventDialogOpen(true);
  };

  const handleCloseNewEventDialog = () => {
    setIsNewEventDialogOpen(false);
    setCurrentEventId(undefined);
    setDefaultEventDate(null);
  };

  // Manejadores para editar eventos existentes
  const handleOpenEditEventDialog = (eventId: number) => {
    setCurrentEventId(eventId);
    setIsEditEventDialogOpen(true);
  };

  const handleCloseEditEventDialog = () => {
    setIsEditEventDialogOpen(false);
    setCurrentEventId(undefined);
  };

  const handleEditEvent = (eventId: string) => {
    if (eventId.startsWith("project-")) {
      const rest = eventId.replace("project-", "");
      const projectId = rest.includes("-") ? rest.slice(0, rest.indexOf("-")) : rest;
      setLocation(`/projects/${projectId}`);
      return;
    }
    const numericEventId = parseInt(eventId, 10);
    handleOpenEditEventDialog(numericEventId);
  };

  // Reassignment mutation
  const assignEstimateMutation = useMutation({
    mutationFn: async (assignmentData: any) => {
      // Update the appointment with the new agent
      const eventResponse = await fetch(`/api/protected/events-patch?id=${assignmentData.appointmentId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          agentId: parseInt(assignmentData.newAgentId)
        }),
      });
      
      if (!eventResponse.ok) {
        const error = await eventResponse.json();
        throw new Error(error.message || 'Failed to update appointment');
      }
      
      return await eventResponse.json();
    },
    onSuccess: () => {
      toast({
        title: "Agent Updated",
        description: "The appointment has been successfully reassigned to the new agent.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/events'] });
      setAssignmentData({
        selectedDate: new Date(2025, 8, 1).toISOString().split("T")[0], // September 2025
        appointmentId: '',
        newAgentId: ''
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Assignment Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAssignSubmit = () => {
    if (!assignmentData.appointmentId || !assignmentData.newAgentId) {
      toast({
        title: "Missing Information",
        description: "Please select an appointment and a new agent.",
        variant: "destructive",
      });
      return;
    }

    const assignmentPayload = {
      appointmentId: assignmentData.appointmentId,
      newAgentId: assignmentData.newAgentId
    };

    assignEstimateMutation.mutate(assignmentPayload);
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
            <div className="w-full space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Calendar & Scheduling</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage appointments and field agents.</p>
              </div>

            <Tabs defaultValue="calendar" className="w-full space-y-4">
              <TabsList className="flex gap-1 border-b border-[var(--dash-border)] bg-transparent p-0 h-auto rounded-none">
                <TabsTrigger value="calendar" className="dash-tab-active data-[state=inactive]:dash-tab-inactive rounded-t-lg px-4 py-2.5">
                  <CalendarIcon className="h-4 w-4 mr-2" />Calendar
                </TabsTrigger>
                <TabsTrigger value="reassign" className="dash-tab-active data-[state=inactive]:dash-tab-inactive rounded-t-lg px-4 py-2.5">
                  <UserCheck className="h-4 w-4 mr-2" />Reassign Appointments
                </TabsTrigger>
                <TabsTrigger value="agents" className="dash-tab-active data-[state=inactive]:dash-tab-inactive rounded-t-lg px-4 py-2.5">
                  <Users className="h-4 w-4 mr-2" />Agent Management
                </TabsTrigger>
              </TabsList>

            <TabsContent value="calendar" className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Calendar left panel: mini-calendar, Scheduled, My Calendars, Categories */}
                <div className="hidden md:block md:w-[280px] md:flex-shrink-0">
                  <CalendarSidebar
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setCurrentDate(date);
                    }}
                    onNavigateMonth={(dir) => setCurrentDate(dir === 1 ? addMonths(currentDate, 1) : subMonths(currentDate, 1))}
                    hasEventsOnDate={hasEventsOnDate}
                    myCalendars={myCalendarsList}
                    onCalendarToggle={handleCalendarToggle}
                    categories={CATEGORIES}
                    selectedCategoryId={selectedCategoryId}
                    onCategorySelect={setSelectedCategoryId}
                    onAddSchedule={() => handleOpenNewEventDialog()}
                    onAddWorker={() => setLocation("/agents")}
                  />
                </div>

                <div className="flex-1 min-w-0 space-y-4">
              {/* Calendar toolbar: search, Today, + New Appointment, New Client */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)]">
                <div className="flex-1 min-w-[160px] max-w-sm">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dash-text-muted)]" />
                    <Input placeholder="Search for anything" value={calendarSearch} onChange={(e) => setCalendarSearch(e.target.value)} className="pl-9 h-9 rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-lg border-[var(--dash-border)]" onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()); }}>Today</Button>
                <Button className="h-9 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white px-4" onClick={() => handleOpenNewEventDialog()}>
                  <Plus className="h-4 w-4 mr-2" />New Appointment
                </Button>
                <Button variant="outline" size="sm" className="h-9 rounded-lg border-[var(--dash-border)]" onClick={() => setIsClientFormOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />New Client
                </Button>
              </div>

              {/* Category filter chips - always visible (Calendar | Calendar Projects) */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-[var(--dash-text-muted)] mr-1">Categories:</span>
                <button
                  type="button"
                  onClick={() => setSelectedCategoryId(null)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${selectedCategoryId !== "projects" ? "bg-[var(--dash-accent)] text-white border-[var(--dash-accent)]" : "border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-bg)]"}`}
                >
                  Calendar
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedCategoryId(selectedCategoryId === "projects" ? null : "projects")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition flex items-center gap-1.5 ${selectedCategoryId === "projects" ? "bg-emerald-600 text-white border-emerald-600" : "border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-bg)]"}`}
                >
                  <span className="w-2 h-2 rounded-full bg-current opacity-80" aria-hidden />
                  Calendar Projects
                </button>
              </div>

              {/* Agent Color Legend - compact, optional */}
              {agents.length > 0 && (
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6 shadow-[var(--dash-shadow)]">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-[var(--dash-text-muted)]">Agent Assignments:</span>
                    {agents.map((agent: any) => (
                      <div key={agent.id} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full border-2 border-[var(--dash-border)] shadow-sm flex-shrink-0" 
                          style={{ backgroundColor: agent.colorCode || '#3B82F6' }}
                          title={agent.teamRole ? `Team: ${agent.teamRole}` : undefined}
                        />
                        <span className="text-sm text-[var(--dash-text)]">
                          {agent.firstName} {agent.lastName}
                        </span>
                        {agent.teamRole && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--dash-bg)] text-[var(--dash-text-muted)] border border-[var(--dash-border)]">
                            Team
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-[var(--dash-text-muted)] border-2 border-[var(--dash-border)] shadow-sm" />
                      <span className="text-sm text-[var(--dash-text)]">Unassigned</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* View-scoped events - title matches Month/Week/Day selection */}
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6 mb-6 shadow-[var(--dash-shadow)]">
                <h3 className="text-lg font-semibold text-[var(--dash-text)] mb-4">
                  {view === "month" ? "Month's Events" : view === "week" ? "Week's Events" : "Today's Events"}
                </h3>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--dash-accent)]" />
                    <p className="text-[var(--dash-text-muted)]">Loading...</p>
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">📅</div>
                    <div className="font-medium text-[var(--dash-text)]">
                      {view === "month" ? "No Events This Month" : view === "week" ? "No Events This Week" : "No Events Today"}
                    </div>
                    <div className="text-sm text-[var(--dash-text-muted)]">Enjoy your time or add some events</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filteredEvents.slice(0, 8).map((event) => (
                      <div key={event.id} className="p-3 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-card)] hover:border-[var(--dash-accent)] transition-all duration-300">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs`} style={{ backgroundColor: event.timeColor }}>
                            {event.time}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-[var(--dash-text)] text-sm truncate">{event.title}</h4>
                            <p className="text-[var(--dash-text-muted)] text-xs truncate">{event.location}</p>
                            {event.contact && (
                              <div className="text-[var(--dash-text-muted)] text-xs">
                                <p className="font-medium truncate">{event.contact.name}</p>
                                {event.contact.phone && (
                                  <p className="truncate">{formatPhone(event.contact.phone)}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Main Calendar Section - Now Full Width */}
              <div className="w-full">
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6 shadow-[var(--dash-shadow)]">
                    {/* Calendar Header - Responsive */}
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
                      {/* Navigation Controls */}
                      <div className="flex items-center gap-2 order-2 sm:order-1">
                        <Button variant="outline" size="sm" onClick={navigatePrev} className="border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <h2 className="text-base sm:text-lg lg:text-xl font-semibold min-w-[180px] sm:min-w-[200px] text-center px-2 text-[var(--dash-text)]">
                          {view === "day" 
                            ? format(safeCurrentDate, "MMM d, yyyy")
                            : view === "week"
                              ? `${format(startOfWeek(safeCurrentDate), "MMM d")} - ${format(endOfWeek(safeCurrentDate), "MMM d")}`
                              : format(safeCurrentDate, "MMMM yyyy")
                          }
                        </h2>
                        <Button variant="outline" size="sm" onClick={navigateNext} className="border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* View Toggle Buttons - Month first (default) */}
                      <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
                        <Button 
                          variant={view === "month" ? "default" : "outline"} 
                          size="sm"
                          onClick={() => setView("month")}
                          className={`text-xs sm:text-sm px-2 sm:px-3 ${
                            view === "month" 
                              ? "bg-[var(--dash-accent)] text-white" 
                              : "border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                          }`}
                        >
                          Month
                        </Button>
                        <Button 
                          variant={view === "week" ? "default" : "outline"} 
                          size="sm"
                          onClick={() => setView("week")}
                          className={`text-xs sm:text-sm px-2 sm:px-3 ${
                            view === "week" 
                              ? "bg-[var(--dash-accent)] text-white" 
                              : "border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                          }`}
                        >
                          Week
                        </Button>
                        <Button 
                          variant={view === "day" ? "default" : "outline"} 
                          size="sm"
                          onClick={() => setView("day")}
                          className={`text-xs sm:text-sm px-2 sm:px-3 ${
                            view === "day" 
                              ? "bg-[var(--dash-accent)] text-white" 
                              : "border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                          }`}
                        >
                          Day
                        </Button>
                      </div>
                    </div>

                    {/* Loading indicator */}
                    {isLoading ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-[var(--dash-accent)]" />
                        <p className="text-[var(--dash-text-muted)]">Loading events...</p>
                      </div>
                    ) : (
                      <div>
                        {/* Month View - Responsive */}
                        {view === "month" && (
                          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                            {/* Day headers */}
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
                              <div key={day} className="text-center text-xs sm:text-sm font-medium py-1 sm:py-2 text-[var(--dash-text-muted)]">
                                <span className="hidden sm:inline">{day}</span>
                                <span className="sm:hidden">{day.charAt(0)}</span>
                              </div>
                            ))}
                            
                            {/* Calendar days */}
                            {calendarDays.map((day, index) => {
                              if (!day || !(day instanceof Date) || isNaN(day.getTime())) {
                                return <div key={`empty-${index}`} className="p-1 sm:p-2 border border-transparent" />;
                              }
                              const dayEvents = getEventsForDay(day);
                              const isCurrentMonth = isSameMonth(day, safeCurrentDate);
                              const isSelected = selectedDate && isSameDay(day, selectedDate);
                              const dayHasEvents = dayEvents.length > 0;
                              const maxPreview = typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 2;
                              return (
                                <div
                                  key={day.toISOString()}
                                  className={`min-h-[60px] sm:min-h-[80px] lg:min-h-[100px] p-0.5 sm:p-1 border rounded cursor-pointer transition ${
                                    isCurrentMonth ? "bg-[var(--dash-card)]" : "bg-[var(--dash-bg)] text-[var(--dash-text-muted)]"
                                  } ${
                                    isSelected ? "border-[var(--dash-accent)]" : "border-[var(--dash-border)] hover:border-[var(--dash-text-muted)]"
                                  } ${
                                    isToday(day) ? "font-bold" : ""
                                  }`}
                                  onClick={() => {
                                    setSelectedDate(day);
                                    handleOpenNewEventDialog(day);
                                  }}
                                >
                                  <div className="flex justify-between items-center mb-1">
                                    <span className={`text-xs sm:text-sm p-0.5 sm:p-1 rounded-full w-4 h-4 sm:w-6 sm:h-6 flex items-center justify-center ${
                                      isSelected ? "bg-blue-500 text-white ring-2 ring-blue-400 ring-offset-1" : isToday(day) ? "bg-[var(--dash-accent)] text-white" : "text-[var(--dash-text)]"
                                    }`}>
                                      {format(day, "d")}
                                    </span>
                                    {dayHasEvents && (
                                      <div className="flex gap-0.5">
                                        {dayEvents.slice(0, 3).map((event, idx) => (
                                          <span
                                            key={`${String(event.id)}-${idx}`}
                                            className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full border border-[var(--dash-border)] shadow-sm"
                                            style={{ backgroundColor: event.timeColor }}
                                            title={event.agentName ? `${event.title || ""} - ${event.agentName}` : `${event.title || ""} - Unassigned`}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="space-y-0.5 sm:space-y-1 hidden sm:block">
                                    {dayEvents.slice(0, maxPreview).map((event) => (
                                      <div
                                        key={String(event.id)}
                                        className="text-xs px-1 py-0.5 truncate rounded bg-[var(--dash-accent)]/20 text-[var(--dash-accent)] cursor-pointer hover:bg-[var(--dash-accent)]/30"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditEvent(String(event.id));
                                        }}
                                        title={`${event.title || ""} - ${event.contact?.name || "No client"} - ${event.time || ""}`}
                                      >
                                        {event.contact?.name || (event.title || "").substring(0, typeof window !== "undefined" && window.innerWidth < 1024 ? 8 : 14) + (event.title && event.title.length > (typeof window !== "undefined" && window.innerWidth < 1024 ? 8 : 14) ? "..." : "")}
                                      </div>
                                    ))}
                                    {dayEvents.length > maxPreview && (
                                      <div className="text-xs text-[var(--dash-text-muted)] px-1">
                                        +{dayEvents.length - maxPreview} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Week View */}
                        {view === "week" && (
                          <div className="space-y-2">
                            {/* Week header with day names */}
                            <div className="grid grid-cols-7 gap-2 mb-2">
                              {eachDayOfInterval({ start: startOfWeek(safeCurrentDate), end: endOfWeek(safeCurrentDate) }).map((day) => {
                                const isSelected = selectedDate && isSameDay(day, selectedDate);
                                return (
                                  <div key={day.toISOString()} className="text-center">
                                    <div className="text-xs sm:text-sm font-medium text-[var(--dash-text-muted)] mb-1">
                                      {format(day, "EEE")}
                                    </div>
                                    <div className={`text-sm sm:text-base font-semibold w-8 h-8 mx-auto flex items-center justify-center rounded-full ${
                                      isSelected ? "bg-blue-500 text-white ring-2 ring-blue-400 ring-offset-2" : isToday(day) ? "bg-[var(--dash-accent)] text-white" : "text-[var(--dash-text)]"
                                    }`}>
                                      {format(day, "d")}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Week grid with events - vertical bar + time + title */}
                            <div className="grid grid-cols-7 gap-2">
                              {eachDayOfInterval({ start: startOfWeek(safeCurrentDate), end: endOfWeek(safeCurrentDate) }).map((day) => {
                                const dayEvents = filteredEvents.filter(event => isSameDay(event.date, day)).sort((a, b) => a.date.getTime() - b.date.getTime());
                                const isSelected = selectedDate && isSameDay(day, selectedDate);
                                return (
                                  <div
                                    key={day.toISOString()}
                                    className={`min-h-[200px] sm:min-h-[300px] p-2 border rounded cursor-pointer ${
                                      isSelected ? "border-blue-500 bg-blue-500/5" : isToday(day) ? "border-[var(--dash-accent)] bg-[var(--dash-accent)]/10" : "border-[var(--dash-border)] bg-[var(--dash-card)]"
                                    } hover:border-[var(--dash-accent)]/50 transition`}
                                    onClick={() => { setSelectedDate(day); handleOpenNewEventDialog(day); }}
                                  >
                                    <div className="space-y-1.5">
                                      {dayEvents.length === 0 ? (
                                        <div className="text-xs text-[var(--dash-text-muted)] text-center py-4">No events</div>
                                      ) : (
                                        dayEvents.map((event) => (
                                          <div
                                            key={event.id}
                                            className="flex items-start gap-2 text-xs rounded-md overflow-hidden border border-[var(--dash-border)] bg-[var(--dash-card)] cursor-pointer hover:border-[var(--dash-accent)]/50 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditEvent(event.id);
                                            }}
                                            title={`${event.title} - ${event.contact?.name || 'No client'} - ${event.time}`}
                                          >
                                            <div className="w-1 flex-shrink-0 self-stretch rounded-full" style={{ backgroundColor: event.timeColor }} aria-hidden />
                                            <div className="min-w-0 flex-1 py-1.5 pr-2">
                                              <div className="font-semibold text-[var(--dash-text)] truncate">{event.time}</div>
                                              <div className="truncate text-[var(--dash-text-muted)]">{event.title}</div>
                                              {event.contact && (
                                                <div className="text-[10px] text-[var(--dash-text-muted)] truncate">{event.contact.name}</div>
                                              )}
                                            </div>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Day View */}
                        {view === "day" && (
                          <div className="space-y-4">
                            {/* Day header */}
                            <div className="text-center mb-4">
                              <div className="text-lg sm:text-xl font-semibold text-[var(--dash-text)] mb-1">
                                {format(safeCurrentDate, "EEEE, MMMM d, yyyy")}
                              </div>
                              {isToday(safeCurrentDate) && (
                                <div className="text-sm text-[var(--dash-accent)]">Today</div>
                              )}
                            </div>
                            
                            {/* Day events list */}
                            <div className="space-y-2">
                              {filteredEvents.length === 0 ? (
                                <div className="text-center py-8 text-[var(--dash-text-muted)]">
                                  <div className="text-4xl mb-2">📅</div>
                                  <div>No events scheduled for this day</div>
                                </div>
                              ) : (
                                filteredEvents
                                  .sort((a, b) => a.date.getTime() - b.date.getTime())
                                  .map((event) => (
                                    <div
                                      key={event.id}
                                      className="p-4 border border-[var(--dash-border)] rounded-lg bg-[var(--dash-card)] hover:border-[var(--dash-accent)] cursor-pointer transition"
                                      onClick={() => handleEditEvent(event.id)}
                                    >
                                      <div className="flex items-start gap-4">
                                        <div
                                          className="w-16 h-16 rounded-lg flex flex-col items-center justify-center text-white font-bold text-sm flex-shrink-0"
                                          style={{ backgroundColor: event.timeColor }}
                                        >
                                          <div>{event.time}</div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h3 className="font-semibold text-[var(--dash-text)] mb-1">{event.title}</h3>
                                          {event.contact && (
                                            <p className="text-sm text-[var(--dash-text-muted)] mb-1">
                                              {event.contact.name}
                                            </p>
                                          )}
                                          <p className="text-sm text-[var(--dash-text-muted)] mb-1">{event.location}</p>
                                          {event.description && (
                                            <p className="text-sm text-[var(--dash-text-muted)] mt-2">{event.description}</p>
                                          )}
                                          {event.agentName && (
                                            <div className="mt-2 flex items-center gap-2">
                                              <div
                                                className="w-3 h-3 rounded-full border border-[var(--dash-border)]"
                                                style={{ backgroundColor: event.timeColor }}
                                              />
                                              <span className="text-xs text-[var(--dash-text-muted)]">{event.agentName}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reassign" className="space-y-4">
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6 shadow-[var(--dash-shadow)]">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--dash-text)] mb-2">Reassign Appointments</h3>
                    <p className="text-[var(--dash-text-muted)]">Change which agent is assigned to existing appointments</p>
                  </div>
                </div>

                <Card className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)]">
                  <CardHeader>
                    <CardTitle className="text-[var(--dash-text)]">Reassign Appointment to Different Agent</CardTitle>
                    <CardDescription className="text-[var(--dash-text-muted)]">Select an existing appointment and change its assigned agent</CardDescription>
                  </CardHeader>
                  

                  <CardContent className="space-y-4">
                    {/* Date Selection - Auto-synced with main calendar */}
                    <div>
                      <Label htmlFor="selectedDate" className="text-[var(--dash-text)]">Calendar Date (Auto-synced)</Label>
                      <Input
                        id="selectedDate"
                        type="date"
                        value={format(currentDate, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          // Create date in local timezone to avoid timezone issues
                          const [year, month, day] = e.target.value.split('-').map(Number);
                          const newDate = new Date(year, month - 1, day);
                          setCurrentDate(newDate);
                        }}
                        className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                      />
                    </div>

                    {/* Available Appointments Section */}
                    <div className="p-4 rounded-md border border-[var(--dash-border)] bg-[var(--dash-card)]">
                      <div className="text-sm font-semibold text-[var(--dash-text)] mb-2">
                        Appointments for {format(currentDate, 'yyyy-MM-dd')}
                      </div>
                      <div className="text-sm text-[var(--dash-text)] space-y-1">
                        {apiEvents && apiEvents.length > 0 ? (
                          apiEvents
                            .filter((event: any) => {
                              if (!event.startTime) return false;
                              const appointmentDate = new Date(event.startTime);
                              return isSameDay(appointmentDate, currentDate);
                            })
                            .slice(0, 5)
                            .map((event: any) => (
                              <div key={event.id}>
                                {event.title} - {new Date(event.startTime).toLocaleTimeString()}
                              </div>
                            ))
                        ) : (
                          <div className="text-[var(--dash-text-muted)]">Loading appointments...</div>
                        )}
                        {apiEvents && apiEvents.filter((event: any) => {
                          if (!event.startTime) return false;
                          const appointmentDate = new Date(event.startTime);
                          return isSameDay(appointmentDate, currentDate);
                        }).length === 0 && (
                          <div className="text-[var(--dash-text-muted)]">No appointments for this date</div>
                        )}
                      </div>
                    </div>

                    {/* Select Appointment */}
                    <div>
                      <Label htmlFor="appointmentId" className="text-[var(--dash-text)]">Select Appointment</Label>
                      <Select
                        value={assignmentData.appointmentId}
                        onValueChange={(value) => setAssignmentData({...assignmentData, appointmentId: value})}
                      >
                        <SelectTrigger className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]">
                          <SelectValue placeholder="Choose an appointment" />
                        </SelectTrigger>
                        <SelectContent>
                          {apiEvents && apiEvents.length > 0 ? (
                            apiEvents
                              .filter((event: any) => {
                                if (!event.startTime) return false;
                                const appointmentDate = new Date(event.startTime);
                                return isSameDay(appointmentDate, currentDate);
                              })
                              .map((event: any) => (
                                <SelectItem key={event.id} value={event.id.toString()}>
                                  {event.title} - {new Date(event.startTime).toLocaleTimeString()}
                                </SelectItem>
                              ))
                          ) : (
                            <SelectItem value="no-appointments" disabled>No appointments available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Select New Agent */}
                    <div>
                      <Label htmlFor="newAgentId" className="text-[var(--dash-text)]">Assign to Agent</Label>
                      <Select
                        value={assignmentData.newAgentId}
                        onValueChange={(value) => setAssignmentData({...assignmentData, newAgentId: value})}
                      >
                        <SelectTrigger className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]">
                          <SelectValue placeholder="Choose a new agent" />
                        </SelectTrigger>
                        <SelectContent>
                          {agents && agents.filter((a: any) => typeof a.id === 'number').length > 0 ? (
                            agents.filter((a: any) => typeof a.id === 'number').map((agent: any) => (
                              <SelectItem key={agent.id} value={agent.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full border border-white shadow-sm flex-shrink-0" 
                                    style={{ backgroundColor: agent.colorCode || getAgentColor(agent.id) }}
                                  />
                                  <span>{agent.firstName} {agent.lastName}{agent.teamRole ? ` (Team)` : ''}</span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-agents" disabled>No agents available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button 
                        onClick={handleAssignSubmit}
                        disabled={assignEstimateMutation.isPending}
                        className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white"
                      >
                        {assignEstimateMutation.isPending ? 'Updating...' : 'Reassign Appointment'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="agents" className="space-y-4">
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--dash-text)] mb-2">Agent Management</h3>
                    <p className="text-[var(--dash-text-muted)]">Manage your field agents and their assignments</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={() => setLocation("/dashboard")}>
                      <Home className="h-4 w-4 mr-2" />
                      Dashboard
                    </Button>
                    <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={() => setLocation("/agents")}>
                      <Users className="h-4 w-4 mr-2" />
                      Full Agent Management
                    </Button>
                  </div>
                </div>
                
                <div className="mb-6">
                  <Button className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white" onClick={() => setLocation("/agents")}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Agent
                  </Button>
                </div>
                
                <AgentScheduler selectedDate={format(currentDate, 'yyyy-MM-dd')} />
              </div>
            </TabsContent>
          </Tabs>

              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden mt-6">
                <div className="px-6 py-4 border-b border-[var(--dash-border)] flex items-center gap-3">
                  <History className="h-5 w-5 text-[var(--dash-accent)]" />
                  <h2 className="text-lg font-semibold text-[var(--dash-text)]">Activity History</h2>
                </div>
                <div className="p-6">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8 text-[var(--dash-text-muted)]">
                      <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-accent)]" />
                      <span className="ml-2">Loading history…</span>
                    </div>
                  ) : (
                    <ActivityHistory activities={activities} isLoading={isLoadingActivities} />
                  )}
                </div>
              </div>
            </div>
        </main>
        </div>
      </div>

      {/* Event Dialogs */}
      <EventDialog
        isOpen={isNewEventDialogOpen}
        onClose={handleCloseNewEventDialog}
        eventId={undefined}
        defaultClientId={defaultClientId}
        defaultType={defaultEventType}
        defaultDate={defaultEventDate}
      />
      
      <EventDialog
        isOpen={isEditEventDialogOpen}
        onClose={handleCloseEditEventDialog}
        eventId={currentEventId}
        defaultDate={currentEventId ? (filteredEvents.find((e) => String(e.id) === String(currentEventId))?.date ?? undefined) : undefined}
      />

      {/* Client Form Dialog */}
      <Dialog open={isClientFormOpen} onOpenChange={setIsClientFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <ClientForm
            onSubmit={(data) => createClientMutation.mutate(data)}
            isSubmitting={createClientMutation.isPending}
            onCancel={() => setIsClientFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Activity History Component
function ActivityHistory({ activities, isLoading }: { activities: any[], isLoading: boolean }) {
  // Activities are already sorted by the API (most recent first)
  const sortedActivities = useMemo(() => {
    if (!activities || activities.length === 0) return [];
    return activities.slice(0, 50); // Limit to 50 most recent
  }, [activities]);
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--dash-accent)]" />
        <p className="text-[var(--dash-text-muted)]">Loading history...</p>
      </div>
    );
  }

  if (sortedActivities.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--dash-text-muted)]">
        <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No activity history yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-1.5 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--dash-border)] scrollbar-track-[var(--dash-bg)]">
      {sortedActivities.map((activity) => {
        const getIcon = () => {
          switch (activity.type) {
            case 'created':
              return <Plus className="h-3.5 w-3.5 text-blue-500" />;
            case 'updated':
              return <Edit className="h-3.5 w-3.5 text-blue-500" />;
            case 'cancelled':
            case 'deleted':
              return <X className="h-3.5 w-3.5 text-red-500" />;
            case 'completed':
              return <CheckCircle className="h-3.5 w-3.5 text-[var(--dash-accent)]" />;
            default:
              return <History className="h-3.5 w-3.5 text-[var(--dash-text-muted)]" />;
          }
        };
        
        const getBadgeColor = () => {
          switch (activity.type) {
            case 'created':
              return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'updated':
              return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'cancelled':
            case 'deleted':
              return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'completed':
              return 'bg-[var(--dash-accent)]/20 text-[var(--dash-accent)] border-[var(--dash-accent)]/30';
            default:
              return 'bg-[var(--dash-text-muted)]/20 text-[var(--dash-text-muted)] border-[var(--dash-border)]';
          }
        };
        
        return (
          <div
            key={activity.id}
            className="flex items-center gap-2.5 px-3 py-2 border border-[var(--dash-border)] rounded-md bg-[var(--dash-card)] hover:bg-[var(--dash-bg)] transition-all duration-150 group"
          >
            <div className="flex-shrink-0">
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <Badge variant="outline" className={`${getBadgeColor()} text-[10px] px-1.5 py-0.5 font-medium border`}>
                {activity.type ? activity.type.charAt(0).toUpperCase() + activity.type.slice(1) : 'Activity'}
              </Badge>
              <p className="text-xs text-[var(--dash-text)] truncate flex-1">
                {activity.description || (activity.title ? `Event "${activity.title}" ${activity.type || 'activity'}${activity.clientName ? ` for ${activity.clientName}` : ''}` : 'Activity')}
              </p>
              <span className="text-[10px] text-[var(--dash-text-muted)] whitespace-nowrap flex-shrink-0">
                {activity.timestamp ? formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true }) : 'Recently'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}// Force Vercel deployment - Sun Sep  7 23:33:30 EDT 2025
